import { useCallback, useRef } from 'react'
import { GridApi, RowDragEndEvent } from 'ag-grid-community'
import { useDragStyle } from '../../../containers/BulkSheetView/hooks/gridEvents/rowDrag'
import { useGridContext } from '../../../containers/BulkSheetView/hooks/useGridContext'
import { ProgressValue } from '../../../../domain/value-object/ProjectPlanSnapshot'
import { ProgressReportTableContent } from '../components/table/ProgressReportTable'

type Params = {
  gridApi: GridApi | undefined
  progressValues: ProgressValue[]
  setProgressValues: (values: ProgressValue[]) => void
  saveTableRows: (uuids: string[]) => void
}

const INITIAL_CONTEXT: Record<string, unknown> = { draggableNodeId: undefined }

export const useProgressReportDragAndDrop = ({
  gridApi,
  progressValues,
  setProgressValues,
  saveTableRows,
}: Params) => {
  // Local state で並び替えを反映後、後続のAPI再フェッチを一度だけスキップする
  const skipNextFetchRef = useRef(false)

  const { contextRef, updateContext } = useGridContext(gridApi, INITIAL_CONTEXT)
  const { onRowDragEnter, onRowDragMove, refreshDragStyle } =
    useDragStyle<ProgressReportTableContent>(gridApi, updateContext)

  const onRowDragEnd = useCallback(
    (event: RowDragEndEvent<ProgressReportTableContent>) => {
      refreshDragStyle()

      const movedUuid = event.node.data?.uuid
      const overIndex = event.overIndex
      if (!movedUuid || overIndex < 0) return

      const currentUuids = progressValues.map(v => v.uuid)
      const fromIndex = currentUuids.indexOf(movedUuid)
      if (fromIndex < 0 || fromIndex === overIndex) return

      const newUuids = [...currentUuids]
      const [moved] = newUuids.splice(fromIndex, 1)
      newUuids.splice(overIndex, 0, moved)

      const reordered = newUuids
        .map(uuid => progressValues.find(v => v.uuid === uuid))
        .filter((v): v is ProgressValue => !!v)

      skipNextFetchRef.current = true
      setProgressValues(reordered)
      saveTableRows(newUuids)
    },
    [progressValues, setProgressValues, saveTableRows, refreshDragStyle]
  )

  return {
    contextRef,
    onRowDragEnter,
    onRowDragMove,
    onRowDragEnd,
    onRowDragLeave: refreshDragStyle,
    skipNextFetchRef,
  }
}
